<template>
  <v-row no-gutters>
    <v-col cols="12" class="py-0">
      <ComponentsFixedBar :minimized-side-menu="minimizedSideMenu" :title="$t('general.notes')"/>
    </v-col>
    <v-col cols="12" class="mb-2" style="padding-top: 50px">
      <v-textarea
        color="kbDarkBlueText"
        :label="$t('general.insertNewNote')"
        v-model="note"
        auto-grow
        rows="1">
        <template v-slot:append>
          <v-btn color="kbDarkBlueText"
                 icon
                 :loading="updatingNotes"
                 @click="addNote()">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
      </v-textarea>
    </v-col>

    <template v-if="notes.length === 0">
      <v-col cols="12" class="d-flex justify-center">
        <div class="px-4 py-0">
          <div class="pb-2 d-flex justify-center align-center">
            <v-icon :color="mainColor" size="40px" class="mr-2">mdi-note-multiple-outline</v-icon>
          </div>
          <span
            class="kbGray--text text-center d-block"
            style="font-size: 18px; width: 100%; margin: 0 auto">
            {{ $t('general.noPersonNotes') }}
          </span>
        </div>
      </v-col>
    </template>

    <template v-else>
      <v-col cols="12" class="mb-2">
        <v-expansion-panels multiple class="mb-2">
          <v-expansion-panel>
            <v-expansion-panel-header color="kbGray" class="white--text">
              {{ $t('general.currentNotes') }}
              <template v-slot:actions>
                <v-icon color="white">$expand</v-icon>
              </template>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="kbLight kbDarkBlueText--text">
              <template>
                <v-list class="kbLight">
                  <!-- Loop through saved notes -->
                  <v-list-item
                    class="mb-1 kbWhite"
                    v-for="(noteItem, index) in notes"
                    :key="index">
                    <v-list-item-content>
                      <v-textarea
                        v-if="editingNotes === index"
                        background-color="kbLight"
                        color="kbDarkBlueText"
                        id="custom-placeholder-color"
                        :placeholder="noteItem.value"
                        solo
                        v-model="editNote"
                        >
                        <template v-slot:append>
                          <v-btn color="kbDarkBlueText" icon @click="findEditedNote(index, editNote)">
                            <v-icon>mdi-content-save</v-icon>
                          </v-btn>
                        </template>
                      </v-textarea>

                      <v-list-item-content v-else>
                        <span style="white-space: pre" class="kbDarkBlueText--text d-block">{{ noteItem.value }}</span>
                      </v-list-item-content>

                      <v-list-item-title>
                        <div
                          class="kbGrayText--text"
                          style="font-size: 12px; text-decoration: none"
                        >{{ noteItem.user.name }}</div>

                        <!-- Displaying note creation date -->
                        <span
                          class="kbGrayText--text"
                          style="font-size: 12px"
                        >{{ $t('general.date') }}: {{ noteItem.date }} -</span>

                        <!-- Displaying note creation hour -->
                        <span
                          class="kbGrayText--text"
                          style="font-size: 12px"
                        >&nbsp;{{ $t('general.hour') }}: {{ noteItem.hour }}</span>
                      </v-list-item-title>
                    </v-list-item-content>

                    <!-- Icon to edit a note -->
                    <v-list-item-icon class="d-flex align-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn color="gray" class="mr-1" icon v-on="on" @click="editingNotes = index">
                            <v-icon>mdi-circle-edit-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('general.editNote') }}</span>
                      </v-tooltip>

                      <!-- Icon to delete a note -->
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn class="mr-1" color="red" icon v-on="on" @click="removeNote(index)">
                            <v-icon>mdi-close-circle-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('general.deleteNote') }}</span>
                      </v-tooltip>
                    </v-list-item-icon>
                  </v-list-item>
                </v-list>
              </template>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </template>
  </v-row>
</template>

<script>
import { mapState } from 'vuex'
import notifications from '@/libs/notifications'
import ComponentsFixedBar from '@/pages/App/People/dialogs/ViewUser/ComponentsFixedBar'
export default {
  components: { ComponentsFixedBar },
  created () {
    this.setExtraData()
  },
  computed: {
    ...mapState({
      selectedUser: state => state.users.selectedUser,
      userKeybe: state => state.auth.userKeybe
    }),
    classifiedNotes () {
      if (this.notes.length === 0) return { notes: [] }
      return this.notes
    }
  },
  data: () => ({
    note: '',
    editNote: '',
    noteDate: new Date().toISOString().substr(0, 10),
    noteHour: new Date().getHours() + ':' + new Date().getMinutes(),
    notes: [],
    notesIndex: -1,
    extraData: [],
    notifications,
    editingNotes: NaN,
    updatingNotes: false
  }),
  destroyed () {
    this.notes = []
    this.tasksIndex = -1
    this.extraData = []
  },
  methods: {
    addNote () {
      if (this.note.trim()) {
        const existingNote = this.notes.find(
          note => note.value.toLowerCase() === this.note.toLowerCase()
        )
        if (existingNote === undefined) {
          this.notes.push({
            value: this.note,
            date: this.noteDate,
            hour: this.noteHour,
            user: {
              UUID: this.selectedUser.UUID,
              name: this.userKeybe.name || '',
              email: this.userKeybe.email || ''
            }
          })
          this.updateNotes()
        }
      }
      this.note = ''
    },
    findEditedNote (index, editValue) {
      this.notes[index].value = editValue
      this.updateNotes()
    },
    findNotes (extraData) {
      const notes = extraData.find(item => item.field === 'userNotesModule')
      this.notesIndex = extraData.map(item => item.field).indexOf('userNotesModule')
      if (notes && typeof notes.value === 'string' && notes.value.includes('[')) {
        this.notes = JSON.parse(notes.value)
      } else {
        this.notes = []
      }
    },
    removeNote (index) {
      this.notes.splice(index, 1)
      this.updateNotes()
    },
    setExtraData () {
      const { extraData } = this.selectedUser
      if (
        extraData &&
        Array.isArray(extraData) &&
        extraData.length > 0
      ) {
        this.extraData = extraData
        this.findNotes(extraData)
      } else {
        this.extraData = []
        this.notes = []
      }
    },
    async updateNotes () {
      this.updatingNotes = true
      const extraData = [...this.extraData]
      if (this.notesIndex === -1) {
        extraData.push({
          field: 'userNotesModule',
          value: this.notes
        })
      } else {
        extraData[this.notesIndex].value = this.notes
      }
      try {
        await this.$store.dispatch('updateUser', {
          userId: this.selectedUser.userId,
          extraData
        })
        await this.$store.dispatch('getUser', {
          userId: this.selectedUser.UUID
        })

        this.setExtraData()
        this.updatingNotes = false
        this.noteDate = new Date().toISOString().substr(0, 10)
        this.noteHour = new Date().getHours() + ':' + new Date().getMinutes()
        this.editNote = ''
        this.editingNotes = NaN
      } catch (e) {
        notifications.error(this.$t('general.errorUpdatingNotes'))
        this.updatingNotes = false
        this.noteDate = new Date().toISOString().substr(0, 10)
        this.noteHour = new Date().getHours() + ':' + new Date().getMinutes()
        this.editNote = ''
        this.editingNotes = NaN
        throw e
      }
    }
  },
  props: {
    mainColor: {
      type: String
    },
    minimizedSideMenu: {
      type: Boolean,
      required: true
    }
  }
}
</script>

<style scoped>
  ::v-deep.v-expansion-panel-content .v-expansion-panel-content__wrap {
    padding: 16px !important;
  }
</style>
